import { ethers } from "ethers";
import { runPreparedCarbonOperation } from "../execution/preparedOperationOrchestrator";
import {
  isOperationVerificationPendingError,
  isReconcileOnlyOperationState,
  OperationVerificationPendingError,
} from "../execution/operationReconciliation";
import {
  assertNoForbiddenAuthorityFields,
  normalizeStablecoinAllowanceResponse,
} from "../api/schemas";
import { resolveCarbonV2ReleaseManifest } from "../read/v2ReadManifest";

const UINT_RE = /^(0|[1-9]\d*)$/;
const ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const BYTES32_RE = /^0x[a-fA-F0-9]{64}$/;
export const CARBON_RECEIPT_DECIMALS = 2;
const ERC1155_READ_INTERFACE = new ethers.utils.Interface([
  `${"func"}tion balanceOf(address account, uint256 id) view returns (uint256)`,
]);

export const toUintString = (value, label, { allowZero = false } = {}) => {
  if (typeof value !== "string") {
    throw new Error(`${label} must be an integer string`);
  }
  const text = value.trim();
  if (!UINT_RE.test(text)) {
    throw new Error(`${label} must be an integer string`);
  }
  if (!allowZero && BigInt(text) === 0n) {
    throw new Error(`${label} must be greater than zero`);
  }
  return text;
};

const toSafeUint = (value, label, { min = 0, max = 255 } = {}) => {
  const text = String(value);
  if (!UINT_RE.test(text)) {
    throw new Error(`${label} must be an integer`);
  }
  const number = Number(text);
  if (!Number.isSafeInteger(number) || number < min || number > max) {
    throw new Error(`${label} is out of range`);
  }
  return number;
};

export const normalizeStablecoinAllowance = (raw) =>
  normalizeStablecoinAllowanceResponse(raw);

const canonicalJson = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

const fnv1a32 = (text) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

export const buildCarbonLifecycleIdempotencyKey = (scope, payload = {}) => {
  assertNoForbiddenAuthorityFields(payload, "idempotency payload");
  const digest = fnv1a32(canonicalJson({ scope, payload }));
  return `carbon-${scope}-${digest}`;
};

export const encodeReceiptId = (year, stage) => {
  const y = toSafeUint(year, "year", { min: 1, max: 255 });
  const s = toSafeUint(stage, "stage", { min: 0, max: 255 });
  return String((BigInt(y) << 8n) | BigInt(s));
};

export const resolveV2Protocol = (project = {}) => {
  if (!project || typeof project !== "object") {
    return null;
  }
  const data = project.data && typeof project.data === "object" ? project.data : {};
  return data.v2_protocol ?? null;
};

export const isCurrentV2CarbonProject = (project = {}) => {
  return Boolean(
    resolveV2Protocol(project)?.protocolVersion === "V2"
  );
};

export const resolveV2ProjectAddress = (project) => {
  if (typeof project === "string") {
    if (!project) throw new Error("project address is required");
    return project;
  }
  const protocol = resolveV2Protocol(project);
  if (!protocol) {
    throw new Error("V2 protocol data is required");
  }
  const address = protocol.projectAddress;
  if (!address) {
    throw new Error("V2 project address is required");
  }
  return address;
};

export const resolveV2ConfigHash = (project) => {
  const protocol = resolveV2Protocol(project);
  if (!protocol) {
    throw new Error("V2 protocol data is required");
  }
  const configHash = protocol.semanticConfigHash;
  if (!BYTES32_RE.test(String(configHash || ""))) {
    throw new Error("V2 semantic config hash is required");
  }
  return String(configHash).toLowerCase();
};

export const resolveV2ReceiptAddress = (project) => {
  const protocol = resolveV2Protocol(project);
  if (!protocol) {
    throw new Error("V2 protocol data is required");
  }
  const address = protocol.receiptAddress;
  if (!ADDRESS_RE.test(String(address))) {
    throw new Error("V2 receipt address is required");
  }
  return address;
};

export const resolveV2YearStageCounts = (project = {}) => {
  const protocol = resolveV2Protocol(project);
  if (!protocol) {
    throw new Error("V2 protocol data is required");
  }
  const rawCounts = protocol.yearStageCounts;
  if (Array.isArray(rawCounts)) {
    return rawCounts.map((count) =>
      toSafeUint(count, "stage count", { min: 0, max: 255 })
    );
  }
  throw new Error("V2 year stage counts are required");
};

const decimalParts = (value, label) => {
  if (typeof value !== "string") {
    throw new Error(`${label} must be a decimal string`);
  }
  const text = value.trim();
  if (!/^(0|[1-9]\d*)(\.\d+)?$/.test(text)) {
    throw new Error(`${label} must be a non-negative decimal string`);
  }
  const [whole, fraction = ""] = text.split(".");
  return { whole, fraction };
};

export const decimalToBaseUnits = (value, decimals, label) => {
  const { whole, fraction } = decimalParts(String(value), label);
  if (fraction.length > decimals) {
    throw new Error(`${label} has too many decimal places`);
  }
  return `${whole}${fraction.padEnd(decimals, "0")}`.replace(/^0+(?=\d)/, "");
};

const unitFactorForDecimals = (decimals) => 10n ** BigInt(decimals);

export const formatReceiptUnitsForDisplay = (
  unitsRaw,
  decimals = CARBON_RECEIPT_DECIMALS
) => {
  const raw = BigInt(toUintString(String(unitsRaw), "unitsRaw", {
    allowZero: true,
  }));
  const factor = unitFactorForDecimals(decimals);
  const whole = raw / factor;
  const fraction = raw % factor;
  if (decimals === 0) return whole.toString();
  if (fraction === 0n) return whole.toString();
  return `${whole.toString()}.${fraction
    .toString()
    .padStart(decimals, "0")
    .replace(/0+$/, "")}`;
};

export const displayReceiptUnitsToRawUnits = (
  amount,
  decimals = CARBON_RECEIPT_DECIMALS
) => toUintString(decimalToBaseUnits(String(amount), decimals, "receipt units"), "units");

const decimalPriceToRawReceiptUnitPrice = ({
  value,
  stablecoinDecimals,
  receiptDecimals,
  label,
}) => {
  const price = BigInt(decimalToBaseUnits(value, stablecoinDecimals, label));
  const factor = unitFactorForDecimals(receiptDecimals);
  if (price === 0n) {
    throw new Error(`${label} must be greater than zero`);
  }
  if (price % factor !== 0n) {
    throw new Error(`${label} does not convert exactly to receipt base units`);
  }
  return price / factor;
};

const rawReceiptUnitPrice = (stagePrice, decimals) =>
  decimalPriceToRawReceiptUnitPrice({
    value: stagePrice,
    stablecoinDecimals: 6,
    receiptDecimals: decimals,
    label: "stagePrice",
  });

const rawMarketplaceUnitPrice = ({
  unitPrice,
  stablecoinDecimals,
  receiptDecimals,
}) =>
  decimalPriceToRawReceiptUnitPrice({
    value: String(unitPrice),
    stablecoinDecimals,
    receiptDecimals,
    label: "unitPrice",
  });

const stableAmountForRawReceiptUnits = ({
  unitsRaw,
  stagePrice,
  receiptDecimals,
}) => String(BigInt(unitsRaw) * rawReceiptUnitPrice(stagePrice, receiptDecimals));

const decimalToScaledBigInt = (value, label) => {
  const { whole, fraction } = decimalParts(String(value), label);
  const scale = 10n ** BigInt(fraction.length);
  return {
    value: BigInt(`${whole}${fraction}`),
    scale,
  };
};

export const decimalTimesUintToBaseUnits = (
  decimalValue,
  uintValue,
  decimals,
  label
) => {
  const parsed = decimalToScaledBigInt(decimalValue, label);
  const units = BigInt(toUintString(String(uintValue), "units"));
  const numerator = parsed.value * units * (10n ** BigInt(decimals));
  if (numerator % parsed.scale !== 0n) {
    throw new Error(`${label} does not convert exactly`);
  }
  return String(numerator / parsed.scale);
};

export const divideDecimalsToWholeUnits = (amount, unitPrice) => {
  const total = decimalToScaledBigInt(String(amount), "amount");
  const price = decimalToScaledBigInt(String(unitPrice), "stagePrice");
  if (price.value === 0n) {
    throw new Error("stagePrice must be greater than zero");
  }
  const numerator = total.value * price.scale;
  const denominator = price.value * total.scale;
  if (numerator % denominator !== 0n) {
    throw new Error("amount must buy a whole number of receipt units");
  }
  return toUintString(String(numerator / denominator), "units");
};

export const buildInvestmentAmounts = ({
  amount,
  isCreditUnits = false,
  stagePrice,
  stablecoinDecimals = 6,
  receiptDecimals = CARBON_RECEIPT_DECIMALS,
} = {}) => {
  if (typeof amount !== "string") {
    throw new Error("amount must be a decimal string");
  }
  if (typeof stagePrice !== "string") {
    throw new Error("stagePrice must be a decimal string");
  }
  if (isCreditUnits) {
    const units = displayReceiptUnitsToRawUnits(amount, receiptDecimals);
    return {
      units,
      requiredStableAmount: stableAmountForRawReceiptUnits({
        unitsRaw: units,
        stagePrice,
        receiptDecimals,
      }),
    };
  }
  const total = BigInt(decimalToBaseUnits(amount, stablecoinDecimals, "amount"));
  const unitPrice = rawReceiptUnitPrice(stagePrice, receiptDecimals);
  const units = toUintString(String(total / unitPrice), "units");
  return {
    units,
    requiredStableAmount: String(BigInt(units) * unitPrice),
  };
};

export const buildMarketplacePurchaseAmounts = ({
  amount,
  isCreditUnits = false,
  unitPrice,
  stablecoinDecimals = 6,
  receiptDecimals = CARBON_RECEIPT_DECIMALS,
} = {}) => {
  const priceText = toUintString(String(unitPrice), "unitPrice");
  const price = BigInt(priceText);
  if (isCreditUnits) {
    const units = displayReceiptUnitsToRawUnits(amount, receiptDecimals);
    return {
      units,
      maxTotalCost: String(BigInt(units) * price),
    };
  }
  const maxTotalCost = decimalToBaseUnits(
    String(amount),
    stablecoinDecimals,
    "amount"
  );
  const total = BigInt(maxTotalCost);
  if (total % price !== 0n) {
    throw new Error("amount must buy a whole number of receipt units");
  }
  return {
    units: toUintString(String(total / price), "units"),
    maxTotalCost,
  };
};

export const buildMarketplaceListingUnitPrice = ({
  unitPrice,
  stablecoinDecimals = 6,
  receiptDecimals = CARBON_RECEIPT_DECIMALS,
} = {}) =>
  String(
    rawMarketplaceUnitPrice({
      unitPrice,
      stablecoinDecimals,
      receiptDecimals,
    })
  );

export const receiptPositionsFromYearCredits = (yearCredits = {}) => {
  const positions = [];
  for (const [year, entries] of Object.entries(yearCredits ?? {})) {
    if (!Array.isArray(entries)) continue;
    for (const entry of entries) {
      const rawUnits = entry?.unitsRaw;
      const stage = entry.stageIndex ?? entry.stage;
      if (rawUnits === undefined || rawUnits === null) {
        throw new Error(
          `V2 receipt position for year ${year} stage ${stage} is missing raw receipt units`
        );
      }
      const units = toUintString(String(rawUnits), "unitsRaw", {
        allowZero: true,
      });
      if (BigInt(units) === 0n) continue;
      if (entry.receiptId === undefined || entry.receiptId === null) {
        throw new Error(
          `V2 receipt position for year ${year} stage ${stage} is missing receiptId`
        );
      }
      const receiptId = toUintString(String(entry.receiptId), "receiptId");
      positions.push({
        year: toSafeUint(year, "year", { min: 1, max: 255 }),
        stage: toSafeUint(stage, "stage", { min: 0, max: 255 }),
        receiptId,
        unitsRaw: units,
        units,
        unitsDisplay: formatReceiptUnitsForDisplay(units),
        maxUnitsRaw: units,
      });
    }
  }
  return positions;
};

export const selectMarketplaceReceiptPosition = ({
  yearCredits = {},
  year = null,
  unitsRaw,
} = {}) => {
  const requestedUnits = toUintString(String(unitsRaw), "unitsRaw");
  const positions = receiptPositionsFromYearCredits(yearCredits).filter(
    (position) => year === null || Number(position.year) === Number(year)
  );
  const position = positions.find(
    (entry) => BigInt(entry.maxUnitsRaw) >= BigInt(requestedUnits)
  );
  if (!position) {
    throw new Error("unitsRaw exceeds latest receipt balance");
  }
  return {
    ...position,
    unitsRaw: requestedUnits,
    units: requestedUnits,
  };
};

export const readV2ReceiptPositions = async ({
  project,
  holder,
  provider,
  years,
} = {}) => {
  if (!ADDRESS_RE.test(String(holder))) {
    throw new Error("holder address is required");
  }
  if (!provider || typeof provider.call !== typeof (() => {})) {
    throw new Error("read provider is required");
  }
  const receiptAddress = resolveV2ReceiptAddress(project);
  const projectAddress = resolveV2ProjectAddress(project);
  const stageCounts = resolveV2YearStageCounts(project);
  const selectedYears =
    Array.isArray(years) && years.length
      ? years.map((year) => toSafeUint(year, "year", { min: 1, max: 255 }))
      : stageCounts.map((_, index) => index + 1);
  const positions = [];
  for (const year of selectedYears) {
    const stageCount = stageCounts[year - 1];
    if (stageCount === undefined) continue;
    for (let stage = 0; stage < stageCount; stage += 1) {
      const receiptId = encodeReceiptId(year, stage);
      const data = ERC1155_READ_INTERFACE.encodeFunctionData("balanceOf", [
        holder,
        receiptId,
      ]);
      const raw = await provider.call({ to: receiptAddress, data });
      const [balance] = ERC1155_READ_INTERFACE.decodeFunctionResult(
        "balanceOf",
        raw
      );
      const unitsRaw = balance.toString();
      if (BigInt(unitsRaw) === 0n) continue;
      positions.push({
        receiptId,
        unitsRaw,
        units: unitsRaw,
        unitsDisplay: formatReceiptUnitsForDisplay(unitsRaw),
        maxUnitsRaw: unitsRaw,
        year,
        stage,
        projectAddress,
      });
    }
  }
  return positions;
};

export const operationStateOf = (operation = {}) =>
  String(operation.state ?? "").toUpperCase();

export const isCompletedOperation = (operation) =>
  operationStateOf(operation) === "COMPLETED";

export const requireCompletedOperation = (operation, label = "operation") => {
  if (isCompletedOperation(operation)) {
    return operation;
  }
  const state = operationStateOf(operation) || "UNKNOWN";
  if (isReconcileOnlyOperationState(state)) {
    throw new OperationVerificationPendingError(operation, label);
  }
  const error = new Error(`${label} did not complete: ${state}`);
  error.operation = operation;
  error.operationState = state;
  throw error;
};

const batchStatusFrom = (results) => {
  if (results.length && results.every((result) => result.state === "COMPLETED")) {
    return "COMPLETED";
  }
  const completedCount = results.filter(
    (result) => result.state === "COMPLETED"
  ).length;
  if (completedCount > 0) {
    return "PARTIALLY_COMPLETED";
  }
  const final = results[results.length - 1]?.state;
  return final === "FAILED" || final === "REJECTED" ? "FAILED" : "NEEDS_REVIEW";
};

export const runReceiptPositionBatch = async ({ positions = [], runPosition }) => {
  const results = [];
  for (const position of positions) {
    try {
      const operation = await runPosition(position);
      const state = operationStateOf(operation) || "NEEDS_REVIEW";
      results.push({ position, operation, state });
      if (state !== "COMPLETED") break;
    } catch (error) {
      const state =
        error.operationState || operationStateOf(error.operation) || "FAILED";
      results.push({ position, operation: error.operation, state, error });
      break;
    }
  }
  const completedCount = results.filter(
    (result) => result.state === "COMPLETED"
  ).length;
  const latestResult = results[results.length - 1] ?? null;
  return {
    status: batchStatusFrom(results),
    positions,
    results,
    completedCount,
    requestedCount: positions.length,
    latest: latestResult?.operation ?? null,
    latestResult,
  };
};

const runPreparedOperation = ({
  prepareResponse,
  sdk,
  apiClient,
  runOperation,
  resolveDirectWalletContext,
  beforeSubmit,
  onSubmittedTransaction,
}) => {
  if (runOperation) {
    return runOperation({
      prepareResponse,
      sdk,
      operationClient: apiClient,
      poll: true,
      maxPolls: 5,
      ...(beforeSubmit === undefined ? {} : { beforeSubmit }),
      ...(onSubmittedTransaction === undefined
        ? {}
        : { onSubmittedTransaction }),
    });
  }
  return runPreparedCarbonOperation({
    prepareResponse,
    sdk,
    operationClient: apiClient,
    resolveDirectWalletContext,
    poll: true,
    maxPolls: 5,
    ...(beforeSubmit === undefined ? {} : { beforeSubmit }),
    ...(onSubmittedTransaction === undefined
      ? {}
      : { onSubmittedTransaction }),
  });
};

const prepareAndRunProjectAdminOperation = async ({
  apiClient,
  sdk,
  project,
  idempotencyKey,
  runOperation,
  resolveDirectWalletContext,
  prepare,
  extraBody = {},
  label,
}) => {
  const projectAddress = resolveV2ProjectAddress(project);
  const operation = await runPreparedOperation({
    prepareResponse: await prepare(projectAddress, {
      idempotency_key: idempotencyKey,
      expected_config_hash: resolveV2ConfigHash(project),
      ...extraBody,
    }),
    sdk,
    apiClient,
    runOperation,
    resolveDirectWalletContext,
  });
  requireCompletedOperation(operation, label);
  return operation;
};

export const prepareAndRunProjectApproval = (options) =>
  prepareAndRunProjectAdminOperation({
    ...options,
    prepare: options.apiClient.prepareProjectApproval,
    label: "project approval",
  });

export const prepareAndRunProjectFundingSuspension = (options) => {
  const reasonHash = String(options.reasonHash || "");
  if (
    !BYTES32_RE.test(reasonHash) ||
    reasonHash.toLowerCase() === `0x${"00".repeat(32)}`
  ) {
    throw new Error("funding suspension reason hash must be nonzero bytes32");
  }
  return prepareAndRunProjectAdminOperation({
    ...options,
    prepare: options.apiClient.prepareProjectFundingSuspension,
    extraBody: { reason_hash: reasonHash.toLowerCase() },
    label: "project funding suspension",
  });
};

export const prepareAndRunProjectFundingReactivation = (options) =>
  prepareAndRunProjectAdminOperation({
    ...options,
    prepare: options.apiClient.prepareProjectFundingReactivation,
    label: "project funding reactivation",
  });

const normalizeComparableAddress = (value, label) => {
  try {
    return ethers.utils.getAddress(value);
  } catch {
    throw new Error(`${label} must be a valid address`);
  }
};

const requireBatchInvestmentProjectAuthority = (
  project,
  businessWalletAddress
) => {
  const protocol = resolveV2Protocol(project);
  if (!protocol || protocol.protocolVersion !== "V2") {
    throw new Error("current Carbon V2 protocol data is required");
  }
  const releaseManifest = resolveCarbonV2ReleaseManifest({
    releaseTag: protocol.releaseTag,
    contractsReleaseCommit: protocol.contractsReleaseCommit,
  });
  if (releaseManifest.investmentExecutionVariant !== "batch") {
    throw new Error("batch investment requires an authorized batch release");
  }
  if (!UINT_RE.test(String(protocol.chainId || "")) || BigInt(protocol.chainId) === 0n) {
    throw new Error("current Carbon V2 chainId is required");
  }
  return {
    projectAddress: normalizeComparableAddress(
      resolveV2ProjectAddress(project),
      "project address"
    ),
    businessWalletAddress: normalizeComparableAddress(
      businessWalletAddress,
      "business wallet address"
    ),
    stablecoinAddress: normalizeComparableAddress(
      protocol.stablecoin,
      "stablecoin address"
    ),
    receiptAddress: normalizeComparableAddress(
      protocol.receiptAddress,
      "receipt address"
    ),
    settlementVaultAddress: normalizeComparableAddress(
      protocol.settlementVaultAddress,
      "settlement vault address"
    ),
    chainId: String(protocol.chainId),
    releaseTag: releaseManifest.releaseTag,
    contractsReleaseCommit: releaseManifest.contractsCommit,
  };
};

const requireBatchInvestmentEnvelope = ({
  envelope,
  authority,
  amountType,
  amount,
  phase,
}) => {
  if (!envelope?.investmentPlan) {
    throw new Error(`batch investment ${phase} is incomplete`);
  }
  if (String(envelope.chainId) !== authority.chainId) {
    throw new Error(`batch investment ${phase} chain does not match project`);
  }
  if (
    normalizeComparableAddress(envelope.projectAddress, "projectAddress") !==
    authority.projectAddress
  ) {
    throw new Error(`batch investment ${phase} project address mismatch`);
  }
  if (
    normalizeComparableAddress(
      envelope.businessWalletAddress,
      "businessWalletAddress"
    ) !== authority.businessWalletAddress
  ) {
    throw new Error(`batch investment ${phase} business wallet mismatch`);
  }
  if (
    envelope.releaseTag !== authority.releaseTag ||
    envelope.contractsReleaseCommit !== authority.contractsReleaseCommit
  ) {
    throw new Error(`batch investment ${phase} release mismatch`);
  }
  if (phase === "prepare") {
    if (
      normalizeComparableAddress(envelope.receiptAddress, "receiptAddress") !==
      authority.receiptAddress
    ) {
      throw new Error("batch investment prepare receipt address mismatch");
    }
    if (
      normalizeComparableAddress(
        envelope.settlementVaultAddress,
        "settlementVaultAddress"
      ) !== authority.settlementVaultAddress
    ) {
      throw new Error(
        "batch investment prepare settlement vault address mismatch"
      );
    }
  }
  if (
    envelope.investmentPlan.amountType !== amountType ||
    envelope.investmentPlan.requestedAmount !== amount
  ) {
    throw new Error(`batch investment ${phase} amount binding mismatch`);
  }
  return envelope.investmentPlan;
};

const requireBatchAllowanceAuthority = (raw, authority) => {
  const normalized = normalizeStablecoinAllowance(raw);
  if (
    normalizeComparableAddress(normalized.ownerAddress, "ownerAddress") !==
      authority.businessWalletAddress ||
    normalizeComparableAddress(
      normalized.stablecoinAddress,
      "stablecoinAddress"
    ) !== authority.stablecoinAddress ||
    normalizeComparableAddress(normalized.spenderAddress, "spenderAddress") !==
      authority.projectAddress
  ) {
    throw new Error("batch investment stablecoin allowance authority mismatch");
  }
  return normalized.allowance;
};

const isBatchQuoteChangedError = (error) => {
  const detail = error?.response?.data?.detail;
  return (
    Number(error?.response?.status) === 409 &&
    (detail?.code === "BATCH_QUOTE_CHANGED" ||
      error?.code === "BATCH_QUOTE_CHANGED")
  );
};

const BATCH_PREPARE_RETRY_DELAY_MS = 3000;

const isTransientBatchPrepareProviderError = (error) => {
  const detail = error?.response?.data?.detail;
  const message =
    typeof detail === "string"
      ? detail
      : typeof detail?.message === "string"
      ? detail.message
      : "";
  return (
    Number(error?.response?.status) === 503 &&
    message.trim() === "Chain provider unavailable"
  );
};

const waitForBatchPrepareRetry = () =>
  new Promise((resolve) => {
    setTimeout(resolve, BATCH_PREPARE_RETRY_DELAY_MS);
  });

const requireConfirmedBatchInvestmentOperation = ({
  operation,
  preparedOperationId,
  authority,
  expectedTransactionHash = null,
}) => {
  requireCompletedOperation(operation, "batch investment");
  if (operation.operationId !== preparedOperationId) {
    throw new Error("confirmed batch investment operation id mismatch");
  }
  if (
    operation.operationType !== "V2_CARBON_INVESTMENT" ||
    operation.executionMode !== "DIRECT_WALLET"
  ) {
    throw new Error("confirmed batch investment operation type is invalid");
  }
  if (String(operation.chainId) !== authority.chainId) {
    throw new Error("confirmed batch investment chain mismatch");
  }
  if (
    normalizeComparableAddress(
      operation.projectAddress,
      "confirmed projectAddress"
    ) !== authority.projectAddress ||
    normalizeComparableAddress(
      operation.businessWalletAddress,
      "confirmed businessWalletAddress"
    ) !== authority.businessWalletAddress ||
    normalizeComparableAddress(
      operation.expectedOnchainActor,
      "confirmed expectedOnchainActor"
    ) !== authority.businessWalletAddress
  ) {
    throw new Error("confirmed batch investment authority mismatch");
  }
  if (!BYTES32_RE.test(String(operation.transactionHash || ""))) {
    throw new Error("confirmed batch investment transactionHash is invalid");
  }
  if (
    expectedTransactionHash !== null &&
    String(operation.transactionHash).toLowerCase() !== expectedTransactionHash
  ) {
    throw new Error("confirmed batch investment transactionHash mismatch");
  }
  if (
    !Number.isSafeInteger(operation.blockNumber) ||
    operation.blockNumber < 0
  ) {
    throw new Error("confirmed batch investment blockNumber is invalid");
  }
  return operation;
};

const hydrateConfirmedBatchInvestmentOperation = async ({
  operation,
  preparedOperationId,
  authority,
  apiClient,
  expectedTransactionHash = null,
}) => {
  let confirmed = operation;
  if (
    isCompletedOperation(confirmed) &&
    (confirmed.transactionHash === undefined ||
      confirmed.transactionHash === null ||
      confirmed.blockNumber === undefined ||
      confirmed.blockNumber === null)
  ) {
    if (!apiClient?.fetchOperation) {
      throw new Error("confirmed batch investment operation details are unavailable");
    }
    confirmed = await apiClient.fetchOperation(preparedOperationId);
  }
  return requireConfirmedBatchInvestmentOperation({
    operation: confirmed,
    preparedOperationId,
    authority,
    expectedTransactionHash,
  });
};

const normalizeBatchRecoveryTransactionHash = (value) => {
  const transactionHash = String(value || "");
  if (!BYTES32_RE.test(transactionHash)) {
    throw new Error("batch investment recovery transactionHash is invalid");
  }
  return transactionHash.toLowerCase();
};

const requireBatchRecoveryOperation = ({
  operation,
  recovery,
  authority,
  phase,
  expectedTransactionHash = recovery.transactionHash,
  allowMissingTransactionHash = false,
}) => {
  if (!operation || typeof operation !== "object" || Array.isArray(operation)) {
    throw new Error(`batch investment ${phase} operation is invalid`);
  }
  if (operation.operationId !== recovery.operationId) {
    throw new Error(`batch investment ${phase} operation id mismatch`);
  }
  if (
    operation.operationType !== "V2_CARBON_INVESTMENT" ||
    operation.executionMode !== "DIRECT_WALLET"
  ) {
    throw new Error(`batch investment ${phase} operation type is invalid`);
  }
  if (
    ![
      "READY_TO_SIGN",
      "SUBMITTED",
      "MINED",
      "VERIFYING",
      "COMPLETED",
      "FAILED",
      "NEEDS_REVIEW",
    ].includes(operationStateOf(operation))
  ) {
    throw new Error(`batch investment ${phase} operation state is invalid`);
  }
  if (String(operation.chainId) !== authority.chainId) {
    throw new Error(`batch investment ${phase} chain mismatch`);
  }
  if (
    normalizeComparableAddress(
      operation.projectAddress,
      `${phase} projectAddress`
    ) !== authority.projectAddress ||
    normalizeComparableAddress(
      operation.businessWalletAddress,
      `${phase} businessWalletAddress`
    ) !== authority.businessWalletAddress ||
    normalizeComparableAddress(
      operation.expectedOnchainActor,
      `${phase} expectedOnchainActor`
    ) !== authority.businessWalletAddress
  ) {
    throw new Error(`batch investment ${phase} authority mismatch`);
  }
  const operationTransactionHash =
    operation.transactionHash === undefined ||
    operation.transactionHash === null
      ? null
      : normalizeBatchRecoveryTransactionHash(operation.transactionHash);
  if (!allowMissingTransactionHash && operationTransactionHash === null) {
    throw new Error(
      `batch investment ${phase} transactionHash is unavailable`
    );
  }
  if (
    expectedTransactionHash !== null &&
    operationTransactionHash !== expectedTransactionHash
  ) {
    throw new Error(`batch investment ${phase} transactionHash mismatch`);
  }
  return { operation, transactionHash: operationTransactionHash };
};

const batchRecoveryDefiniteFailure = ({ operation, recovery }) => {
  const error = new Error(
    `batch investment did not complete: ${operationStateOf(operation) || "UNKNOWN"}`
  );
  error.code =
    operation?.normalizedErrorCode === "DIRECT_TX_REPLACED_CANCELLED"
      ? "DIRECT_WALLET_TRANSACTION_CANCELLED"
      : "BATCH_INVESTMENT_TERMINAL_FAILURE";
  error.operation = operation;
  error.operationState = operationStateOf(operation);
  error.batchInvestmentRecovery = recovery;
  error.batchInvestmentDefiniteFailure = true;
  return error;
};

const batchRecoveryPendingError = ({ error, recovery, authority }) => {
  const pending = new OperationVerificationPendingError(
    {
      operationId: recovery.operationId,
      operationType: "V2_CARBON_INVESTMENT",
      executionMode: "DIRECT_WALLET",
      state: String(error?.operation?.state || "VERIFYING").toUpperCase(),
      chainId: authority.chainId,
      projectAddress: authority.projectAddress,
      businessWalletAddress: authority.businessWalletAddress,
      expectedOnchainActor: authority.businessWalletAddress,
      transactionHash: recovery.transactionHash,
    },
    "batch investment"
  );
  pending.batchInvestmentRecovery = recovery;
  pending.cause = error;
  return pending;
};

export const prepareAndRunBatchInvestment = async ({
  apiClient,
  sdk,
  project,
  businessWalletAddress,
  amountType,
  amount,
  idempotencyKey,
  runOperation,
  resolveDirectWalletContext,
  onPlan,
  beforeSubmit,
  onSubmittedTransaction,
  pendingVerification = null,
}) => {
  if (!["stable", "receipt_units"].includes(amountType)) {
    throw new Error("batch investment amountType is invalid");
  }
  const amountText = toUintString(String(amount), "amount");
  const authority = requireBatchInvestmentProjectAuthority(
    project,
    businessWalletAddress
  );
  if (pendingVerification !== null) {
    if (
      !pendingVerification ||
      typeof pendingVerification !== "object" ||
      Array.isArray(pendingVerification) ||
      Object.keys(pendingVerification).length !== 3 ||
      !/^v2-op-[0-9a-f]{32}$/.test(pendingVerification.operationId) ||
      !pendingVerification.investmentPlan ||
      typeof pendingVerification.investmentPlan !== "object"
    ) {
      throw new Error("batch investment pending verification is invalid");
    }
    if (
      !apiClient?.attachTransactionResult ||
      !apiClient?.reconcileAndFetchOperation ||
      !apiClient?.fetchOperation
    ) {
      throw new Error("batch investment reconciliation client is unavailable");
    }
    const recovery = Object.freeze({
      operationId: pendingVerification.operationId,
      transactionHash: normalizeBatchRecoveryTransactionHash(
        pendingVerification.transactionHash
      ),
      investmentPlan: pendingVerification.investmentPlan,
    });
    const recoveredPlan = requireBatchInvestmentEnvelope({
      envelope: {
        chainId: authority.chainId,
        projectAddress: authority.projectAddress,
        businessWalletAddress: authority.businessWalletAddress,
        releaseTag: authority.releaseTag,
        contractsReleaseCommit: authority.contractsReleaseCommit,
        investmentPlan: recovery.investmentPlan,
      },
      authority,
      amountType,
      amount: amountText,
      phase: "verification retry",
    });
    try {
      const fetched = requireBatchRecoveryOperation({
        operation: await apiClient.fetchOperation(recovery.operationId),
        recovery,
        authority,
        phase: "recovery read",
        expectedTransactionHash: null,
        allowMissingTransactionHash: true,
      });
      const fetchedState = operationStateOf(fetched.operation);
      if (fetchedState === "COMPLETED") {
        if (fetched.transactionHash !== recovery.transactionHash) {
          throw new Error(
            "batch investment recovery completed transactionHash mismatch"
          );
        }
        const investment = await hydrateConfirmedBatchInvestmentOperation({
          operation: fetched.operation,
          preparedOperationId: recovery.operationId,
          authority,
          apiClient,
          expectedTransactionHash: recovery.transactionHash,
        });
        return {
          approval: { approvalRequired: false },
          investment,
          investmentPlan: recoveredPlan,
        };
      }
      if (
        fetchedState === "FAILED" &&
        (fetched.operation.normalizedErrorCode ===
          "DIRECT_TX_REPLACED_CANCELLED" ||
          Number(fetched.operation.receiptStatus) === 0)
      ) {
        throw batchRecoveryDefiniteFailure({
          operation: fetched.operation,
          recovery,
        });
      }
      if (["FAILED", "NEEDS_REVIEW"].includes(fetchedState)) {
        throw new Error(
          `batch investment recovery requires review: ${fetchedState}`
        );
      }
      if (
        fetchedState === "READY_TO_SIGN" &&
        fetched.transactionHash !== null
      ) {
        throw new Error(
          "batch investment recovery unsigned operation has a transactionHash"
        );
      }
      let attached = fetched.operation;
      if (fetched.transactionHash === null) {
        if (fetchedState !== "READY_TO_SIGN") {
          throw new Error(
            "batch investment recovery transactionHash is unavailable"
          );
        }
        attached = await apiClient.attachTransactionResult(
          recovery.operationId,
          { transactionHash: recovery.transactionHash }
        );
      } else if (fetched.transactionHash !== recovery.transactionHash) {
        if (fetchedState !== "SUBMITTED") {
          throw new Error(
            "batch investment recovery superseded hash is not replaceable"
          );
        }
        attached = await apiClient.attachTransactionResult(
          recovery.operationId,
          {
            transactionHash: recovery.transactionHash,
            replacesTransactionHash: fetched.transactionHash,
          }
        );
      }
      const attachedRecovery = requireBatchRecoveryOperation({
        operation: attached,
        recovery,
        authority,
        phase: "retry attachment",
      });
      if (
        operationStateOf(attachedRecovery.operation) === "FAILED" &&
        (attachedRecovery.operation.normalizedErrorCode ===
          "DIRECT_TX_REPLACED_CANCELLED" ||
          Number(attachedRecovery.operation.receiptStatus) === 0)
      ) {
        throw batchRecoveryDefiniteFailure({
          operation: attachedRecovery.operation,
          recovery,
        });
      }
      const investment = await hydrateConfirmedBatchInvestmentOperation({
        operation: await apiClient.reconcileAndFetchOperation(
          recovery.operationId
        ),
        preparedOperationId: recovery.operationId,
        authority,
        apiClient,
        expectedTransactionHash: recovery.transactionHash,
      });
      return {
        approval: { approvalRequired: false },
        investment,
        investmentPlan: recoveredPlan,
      };
    } catch (error) {
      if (error?.batchInvestmentDefiniteFailure === true) {
        throw error;
      }
      throw batchRecoveryPendingError({ error, recovery, authority });
    }
  }
  const quoteRequest = {
    amount_type: amountType,
    amount: amountText,
  };
  const readQuote = async (phase = "quote") => {
    const envelope = await apiClient.quoteBatchInvestment(
      authority.projectAddress,
      quoteRequest
    );
    const quotePlan = requireBatchInvestmentEnvelope({
      envelope,
      authority,
      amountType,
      amount: amountText,
      phase,
    });
    const quoteHash = String(envelope?.quoteHash || "");
    if (
      !BYTES32_RE.test(quoteHash) ||
      /^0x0{64}$/i.test(quoteHash)
    ) {
      throw new Error("batch investment quoteHash must be nonzero bytes32");
    }
    await onPlan?.(quotePlan, phase);
    return { envelope, plan: quotePlan, quoteHash: quoteHash.toLowerCase() };
  };
  const quote = await readQuote();
  const approvalBound = toUintString(
    String(quote.plan.allowanceRequired),
    "allowanceRequired"
  );

  const readAllowance = async () =>
    requireBatchAllowanceAuthority(
      await apiClient.getStablecoinAllowance(authority.projectAddress),
      authority
    );
  let currentAllowance = await readAllowance();
  let approval = { approvalRequired: false };
  if (BigInt(currentAllowance) < BigInt(approvalBound)) {
    const preparedApproval = await apiClient.prepareStablecoinApproval(
      authority.projectAddress,
      {
        idempotency_key: `${idempotencyKey}:approval`,
        amount: approvalBound,
      }
    );
    approval =
      preparedApproval?.approvalRequired === false
        ? { approvalRequired: false }
        : await runPreparedOperation({
            prepareResponse: preparedApproval,
            sdk,
            apiClient,
            runOperation,
            resolveDirectWalletContext,
            beforeSubmit,
          });
    if (approval?.approvalRequired !== false) {
      requireCompletedOperation(approval, "stablecoin approval");
    }
    currentAllowance = await readAllowance();
    if (BigInt(currentAllowance) < BigInt(approvalBound)) {
      throw new Error("stablecoin allowance is still below batch quote amount");
    }
  }

  const prepareRequest = {
    idempotency_key: idempotencyKey,
    amount_type: amountType,
    amount: amountText,
    max_stable_amount: approvalBound,
    quote_hash: quote.quoteHash,
  };
  const prepareInvestment = () =>
    apiClient.prepareBatchInvestment(
      authority.projectAddress,
      prepareRequest
    );
  let preparedInvestment;
  try {
    try {
      preparedInvestment = await prepareInvestment();
    } catch (error) {
      if (!isTransientBatchPrepareProviderError(error)) {
        throw error;
      }
      await waitForBatchPrepareRetry();
      preparedInvestment = await prepareInvestment();
    }
  } catch (error) {
    if (isBatchQuoteChangedError(error)) {
      const refreshedQuote = await readQuote("requote");
      error.investmentPlan = refreshedQuote.plan;
    }
    throw error;
  }
  const preparedPlan = requireBatchInvestmentEnvelope({
    envelope: preparedInvestment,
    authority,
    amountType,
    amount: amountText,
    phase: "prepare",
  });
  if (
    BigInt(preparedPlan.totalStablePaid) > BigInt(approvalBound) ||
    BigInt(preparedPlan.allowanceRequired) > BigInt(currentAllowance)
  ) {
    throw new Error("fresh batch investment exceeds the quote approval bound");
  }
  await onPlan?.(preparedPlan, "prepare");
  let submittedInvestment;
  let investment;
  try {
    submittedInvestment = await runPreparedOperation({
      prepareResponse: preparedInvestment,
      sdk,
      apiClient,
      runOperation,
      resolveDirectWalletContext,
      beforeSubmit,
      onSubmittedTransaction:
        onSubmittedTransaction === undefined
          ? undefined
          : (submission) =>
              onSubmittedTransaction({
                ...submission,
                investmentPlan: preparedPlan,
                idempotencyKey,
              }),
    });
    investment = await hydrateConfirmedBatchInvestmentOperation({
      operation: submittedInvestment,
      preparedOperationId: preparedInvestment.operationId,
      authority,
      apiClient,
    });
  } catch (error) {
    if (
      [
        "DIRECT_WALLET_TRANSACTION_REVERTED",
        "DIRECT_WALLET_TRANSACTION_CANCELLED",
      ].includes(error?.code)
    ) {
      error.batchInvestmentDefiniteFailure = true;
      throw error;
    }
    const recoveryOperation = error?.operation || submittedInvestment;
    if (!BYTES32_RE.test(String(recoveryOperation?.transactionHash || ""))) {
      throw error;
    }
    const recovery = Object.freeze({
      operationId: preparedInvestment.operationId,
      transactionHash: normalizeBatchRecoveryTransactionHash(
        recoveryOperation.transactionHash
      ),
      investmentPlan: preparedPlan,
    });
    try {
      requireBatchRecoveryOperation({
        operation: recoveryOperation,
        recovery,
        authority,
        phase: "submitted operation",
      });
    } catch (validationError) {
      throw batchRecoveryPendingError({
        error: validationError,
        recovery,
        authority,
      });
    }
    if (isOperationVerificationPendingError(error)) {
      error.batchInvestmentRecovery = recovery;
      throw error;
    }
    throw batchRecoveryPendingError({ error, recovery, authority });
  }
  return {
    approval,
    investment,
    investmentPlan: preparedPlan,
  };
};

export const prepareAndRunInvestment = async ({
  apiClient,
  sdk,
  project,
  year,
  stage,
  units,
  requiredStableAmount,
  idempotencyKey,
  runOperation,
  resolveDirectWalletContext,
}) => {
  const projectAddress = resolveV2ProjectAddress(project);
  const unitText = toUintString(String(units), "units");
  const amountText = toUintString(
    String(requiredStableAmount),
    "requiredStableAmount"
  );
  const readAllowance = async () =>
    normalizeStablecoinAllowance(
      await apiClient.getStablecoinAllowance(projectAddress)
    ).allowance;
  const allowance = await readAllowance();
  let approval = { approvalRequired: false };
  if (BigInt(allowance) < BigInt(amountText)) {
    const preparedApproval = await apiClient.prepareStablecoinApproval(
      projectAddress,
      {
        idempotency_key: `${idempotencyKey}:approval`,
        amount: amountText,
      }
    );
    approval =
      preparedApproval?.approvalRequired === false
        ? { approvalRequired: false }
        : await runPreparedOperation({
            prepareResponse: preparedApproval,
            sdk,
            apiClient,
            runOperation,
            resolveDirectWalletContext,
          });
    if (approval?.approvalRequired !== false) {
      requireCompletedOperation(approval, "stablecoin approval");
    }
    const refreshedAllowance = await readAllowance();
    if (BigInt(refreshedAllowance) < BigInt(amountText)) {
      throw new Error("stablecoin allowance is still below required amount");
    }
  }
  const preparedInvestment = await apiClient.prepareInvestment(projectAddress, {
    idempotency_key: idempotencyKey,
    year: toSafeUint(year, "year", { min: 1, max: 255 }),
    stage: toSafeUint(stage, "stage", { min: 0, max: 255 }),
    units: unitText,
  });
  const preparedInvestmentState = String(
    preparedInvestment?.operationState ?? preparedInvestment?.state ?? ""
  ).toUpperCase();
  if (
    preparedInvestmentState &&
    !["CONSENT_READY", "READY_TO_SIGN"].includes(preparedInvestmentState)
  ) {
    const investment = await runPreparedOperation({
      prepareResponse: preparedInvestment,
      sdk,
      apiClient,
      runOperation,
      resolveDirectWalletContext,
    });
    requireCompletedOperation(investment, "investment");
    return { approval, investment };
  }
  const investment = await runPreparedOperation({
    prepareResponse: preparedInvestment,
    sdk,
    apiClient,
    runOperation,
    resolveDirectWalletContext,
  });
  requireCompletedOperation(investment, "investment");
  return { approval, investment };
};

const prepareAndRunReceiptOperation = async ({
  apiClient,
  sdk,
  project,
  receiptId,
  units,
  unitsRaw,
  maxUnitsRaw,
  balanceRaw,
  recipient,
  idempotencyKey,
  runOperation,
  prepare,
  includeData = false,
  requireUnitsRaw = false,
}) => {
  const projectAddress = resolveV2ProjectAddress(project);
  if (!ADDRESS_RE.test(String(recipient))) {
    throw new Error("recipient address is required");
  }
  if (requireUnitsRaw && (unitsRaw === undefined || unitsRaw === null)) {
    throw new Error("unitsRaw is required");
  }
  const rawUnits = unitsRaw ?? units;
  const unitsText = toUintString(String(rawUnits), "unitsRaw");
  const maxText =
    maxUnitsRaw === undefined && balanceRaw === undefined
      ? null
      : toUintString(String(maxUnitsRaw ?? balanceRaw), "maxUnitsRaw", {
          allowZero: true,
        });
  if (maxText !== null && BigInt(unitsText) > BigInt(maxText)) {
    throw new Error("unitsRaw exceeds latest receipt balance");
  }
  const body = {
    idempotency_key: idempotencyKey,
    receipt_id: toUintString(String(receiptId), "receiptId"),
    units: unitsText,
    recipient,
  };
  if (includeData) {
    body.data = "0x";
  }
  return runPreparedOperation({
    prepareResponse: await prepare(projectAddress, body),
    sdk,
    apiClient,
    runOperation,
  });
};

export const prepareAndRunCreditClaim = (options) =>
  prepareAndRunReceiptOperation({
    ...options,
    prepare: options.apiClient.prepareCreditClaim,
    requireUnitsRaw: true,
  });

export const prepareAndRunRefundClaim = (options) =>
  prepareAndRunReceiptOperation({
    ...options,
    prepare: options.apiClient.prepareRefundClaim,
    requireUnitsRaw: true,
  });

export const prepareAndRunReceiptTransfer = (options) =>
  prepareAndRunReceiptOperation({
    ...options,
    prepare: options.apiClient.prepareReceiptTransfer,
    includeData: true,
  });

export const prepareAndRunDeveloperWithdrawal = async ({
  apiClient,
  sdk,
  resolveDirectWalletContext,
  project,
  year,
  stage,
  idempotencyKey,
  runOperation,
}) => {
  const projectAddress = resolveV2ProjectAddress(project);
  return runPreparedOperation({
    prepareResponse: await apiClient.prepareDeveloperWithdrawal(
      projectAddress,
      toSafeUint(year, "year", { min: 1, max: 255 }),
      toSafeUint(stage, "stage", { min: 0, max: 255 }),
      { idempotency_key: idempotencyKey }
    ),
    sdk,
    apiClient,
    runOperation,
    resolveDirectWalletContext,
  });
};

export const prepareAndRunTreasuryFeeWithdrawal = async ({
  apiClient,
  sdk,
  resolveDirectWalletContext,
  project,
  idempotencyKey,
  runOperation,
}) => {
  const projectAddress = resolveV2ProjectAddress(project);
  return runPreparedOperation({
    prepareResponse: await apiClient.prepareTreasuryFeeWithdrawal(projectAddress, {
      idempotency_key: idempotencyKey,
    }),
    sdk,
    apiClient,
    runOperation,
    resolveDirectWalletContext,
  });
};

export const prepareAndRunMarketplaceListing = async ({
  apiClient,
  sdk,
  resolveDirectWalletContext,
  project,
  receiptId,
  unitsRaw,
  maxUnitsRaw,
  unitPrice,
  allowedBuyer = null,
  expiresAt = 0,
  idempotencyKey,
  runOperation,
}) => {
  const projectAddress = resolveV2ProjectAddress(project);
  const unitsText = toUintString(String(unitsRaw), "unitsRaw");
  if (maxUnitsRaw !== undefined && maxUnitsRaw !== null) {
    const maxText = toUintString(String(maxUnitsRaw), "maxUnitsRaw", {
      allowZero: true,
    });
    if (BigInt(unitsText) > BigInt(maxText)) {
      throw new Error("unitsRaw exceeds latest receipt balance");
    }
  }
  const preparedApproval = await apiClient.prepareMarketplaceReceiptApproval({
    idempotency_key: `${idempotencyKey}:receipt-approval`,
    project: projectAddress,
    approved: true,
  });
  const approval =
    preparedApproval?.approvalRequired === false
      ? preparedApproval
      : await runPreparedOperation({
          prepareResponse: preparedApproval,
          sdk,
          apiClient,
          runOperation,
          resolveDirectWalletContext,
        });
  if (approval?.approvalRequired !== false) {
    requireCompletedOperation(approval, "marketplace receipt approval");
  }
  const listing = await runPreparedOperation({
    prepareResponse: await apiClient.prepareMarketplaceListing({
      idempotency_key: idempotencyKey,
      project: projectAddress,
      receipt_id: toUintString(String(receiptId), "receiptId"),
      units: unitsText,
      unit_price: toUintString(String(unitPrice), "unitPrice"),
      allowed_buyer: allowedBuyer,
      expires_at: expiresAt,
    }),
    sdk,
    apiClient,
    runOperation,
    resolveDirectWalletContext,
  });
  requireCompletedOperation(listing, "marketplace listing");
  return { approval, listing };
};

export const prepareAndRunMarketplacePurchase = async ({
  apiClient,
  sdk,
  resolveDirectWalletContext,
  listingId,
  project,
  units,
  maxTotalCost,
  deadline = 0,
  recipient,
  idempotencyKey,
  runOperation,
}) => {
  const projectAddress = resolveV2ProjectAddress(project);
  const unitsText = toUintString(String(units), "units");
  const maxTotalCostText = toUintString(String(maxTotalCost), "maxTotalCost");
  if (!ADDRESS_RE.test(String(recipient))) {
    throw new Error("recipient address is required");
  }
  const approval =
    await apiClient.prepareMarketplaceStablecoinApproval({
      idempotency_key: `${idempotencyKey}:stablecoin-approval`,
      project: projectAddress,
      amount: maxTotalCostText,
    });
  const completedApproval =
    approval?.approvalRequired === false
      ? { approvalRequired: false }
      : await runPreparedOperation({
          prepareResponse: approval,
          sdk,
          apiClient,
          runOperation,
          resolveDirectWalletContext,
        });
  if (completedApproval?.approvalRequired !== false) {
    requireCompletedOperation(completedApproval, "marketplace stablecoin approval");
  }
  const purchase = await runPreparedOperation({
    prepareResponse: await apiClient.prepareMarketplacePurchase(String(listingId), {
      idempotency_key: idempotencyKey,
      units: unitsText,
      max_total_cost: maxTotalCostText,
      deadline,
      recipient,
    }),
    sdk,
    apiClient,
    runOperation,
    resolveDirectWalletContext,
  });
  requireCompletedOperation(purchase, "marketplace purchase");
  return { approval: completedApproval, purchase };
};
